import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Breadcrumb } from "@/components/Breadcrumb";
import { MapPin } from "lucide-react";
import { CTAContactForm } from "@/components/CTAContactForm";
import { CTASection } from "@/components/CTASection";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import heroImage from "@/assets/mp-masonry-paving-passaic-nj-041026.webp";
import stepsImage from "@/assets/mp-steps-and-stoops-installation-passaic-nj-041026-2.webp";
import concreteImage from "@/assets/concrete-sidewalk-installation-passaic-nj.webp";
import patioImage from "@/assets/mp-patio-walkway-paving-passaic-nj-041026.webp";
import retainingWallImage from "@/assets/mp-retaining-wall-installation-passaic-nj-050926.webp";

const faqs = [
  {
    question: "Do I need a permit for masonry work in Passaic?",
    answer:
      "Most structural masonry work in Passaic requires a construction permit from the city's Department of Inspection and Code Enforcement at 330 Passaic Street. Retaining walls, new steps and stoops, and driveway modifications typically require a permit application and inspection. A paver patio on your own property usually does not need a building permit, but it may count toward your lot's impervious coverage under the city's zoning code. We can check what applies to your specific project when we come out for the estimate.",
  },
  {
    question: "What type of soil do most Passaic properties sit on?",
    answer:
      "Most of Passaic sits on Boonton series glacial till, which is a clay-heavy soil that holds water and expands when it freezes. That movement is what causes settling, shifting, and cracking in steps, driveways, patios, and walls over time. We account for it by excavating deeper, compacting the base in lifts, and building in drainage so the water has somewhere to go instead of sitting under your project.",
  },
  {
    question: "How long does it take to replace front steps on a Passaic home?",
    answer:
      "Most step and stoop replacements in Passaic take about 3 to 5 working days, depending on the size of the stoop and whether the old footings need to come out completely. On multi-family buildings with larger stoops the timeline can run a little longer. Weather and permit timing can shift things, but we give you a clear schedule before we start and keep you updated if anything changes.",
  },
  {
    question:
      "Do you work on multi-family and rental properties in Passaic?",
    answer:
      "Yes, we work with both homeowners and property owners in Passaic, including two-family and three-family buildings. We understand that rental properties have additional considerations around tenant access and scheduling, and we coordinate with property managers to minimize disruption. If you have received a code enforcement notice about steps, a sidewalk, or another masonry issue, we can come out and give you an estimate quickly.",
  },
  {
    question:
      "When is the best time of year to start a masonry project in Passaic?",
    answer:
      "Spring through early fall, roughly April to November, is the best window because the ground needs to be workable and temperatures need to stay above freezing for concrete and mortar to cure properly. We start booking spring projects in late winter, so if you reach out in February or March you have the best shot at getting on the schedule early in the season before things fill up.",
  },
  {
    question: "Does Masonry Pro serve all of Passaic?",
    answer:
      "Yes, we work across the entire city of Passaic, from Passaic Park and the Third Ward area to the blocks near downtown, Main Avenue, Broadway, and the neighborhoods closer to the river and Dundee Island Park. Our shop in Totowa is about 10 minutes from most parts of the city, and we are in Passaic regularly for both new projects and repairs.",
  },
];

export default function Passaic() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Masonry Contractor in Passaic NJ - Local Crew | Masonry Pro</title>
        <meta name="description" content="Need masonry work in Passaic, NJ? We handle steps, stoops, concrete, patios, and walls for older homes and multi-family properties. Call or text for a quote." />
        <meta property="og:description" content="Need masonry work in Passaic, NJ? We handle steps, stoops, concrete, patios, and walls for older homes and multi-family properties. Call or text for a quote." />
        <meta name="author" content="Masonry Pro Construction" />
        <meta
          name="keywords"
          content="masonry contractor, paver patio, retaining wall, stone work, North Jersey, Bergen County, Essex County, Passaic County"
        />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Masonry Contractor in Passaic NJ - Local Crew | Masonry Pro" />
        <meta
          property="og:description"
          content="Need a masonry contractor in Passaic? Steps, stoops, concrete driveways, sidewalks, retaining walls, and patios from a local crew in Totowa. Free estimates."
        />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://masonry-pro.com/masonry-pro-logo-2.webp" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@MasonryPro" />
        <meta name="twitter:image" content="https://masonry-pro.com/masonry-pro-logo-2.webp" />
        <meta name="twitter:title" content="Masonry Contractor in Passaic NJ - Local Crew | Masonry Pro" />
        <meta
          name="twitter:description"
          content="Need a masonry contractor in Passaic? Steps, stoops, concrete driveways, sidewalks, retaining walls, and patios from a local crew in Totowa. Free estimates."
        />
        <link rel="preload" as="image" href={heroImage} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((faq) => ({
              "@type": "Question",
              name: faq.question,
              acceptedAnswer: {
                "@type": "Answer",
                text: faq.answer,
              },
            })),
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        <Breadcrumb />

        <main>
          {/* Hero Section */}
          <section className="relative">
            <img
              src={heroImage}
              alt="Historical cityscape with masonry buildings and a clock tower in Passaic, Passaic County, NJ. Masonry Contractor Passaic"
              width={1920}
              height={600}
              fetchPriority="high"
              decoding="async"
              className="w-full h-[400px] md:h-[500px] lg:h-[600px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/45 to-black/65 flex items-center justify-center">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto text-center">
                  <div className="flex items-center justify-center gap-2 mb-4">
                    <MapPin
                      className="h-6 w-6"
                      style={{ color: "#d4a853" }}
                    />
                    <span style={{ color: "#d4a853", fontWeight: 500 }}>
                      Passaic County, NJ
                    </span>
                  </div>
                  <h1
                    style={{
                      color: "#f5f5f5",
                      fontSize: "3rem",
                      fontWeight: "bold",
                      lineHeight: 1.1,
                      marginBottom: "1.5rem",
                      textShadow: "0 2px 8px rgba(0,0,0,0.9)",
                    }}
                  >
                    Masonry Contractor in{" "}
                    <span
                      style={{
                        color: "#d4a853",
                        WebkitTextFillColor: "#d4a853",
                      }}
                    >
                      Passaic, NJ
                    </span>
                  </h1>
                  <p style={{ color: '#c4a24e', WebkitTextFillColor: '#c4a24e', fontSize: '0.95rem', fontWeight: 500, letterSpacing: '0.025em', textShadow: '0 1px 4px rgba(0,0,0,0.9)' }}>
                    <Link to="/masonry-services/steps-stoops" style={{ color: '#c4a24e', textDecoration: 'none' }} className="hover:underline">Steps &amp; Stoops</Link> | <Link to="/concrete-services/driveways" style={{ color: '#c4a24e', textDecoration: 'none' }} className="hover:underline">Concrete Driveways</Link> | <Link to="/concrete-services/sidewalks" style={{ color: '#c4a24e', textDecoration: 'none' }} className="hover:underline">Concrete Sidewalks</Link> | <Link to="/paving-services/paver-patios" style={{ color: '#c4a24e', textDecoration: 'none' }} className="hover:underline">Paver Patios</Link> | <Link to="/masonry-services/retaining-walls" style={{ color: '#c4a24e', textDecoration: 'none' }} className="hover:underline">Retaining Walls</Link> | <Link to="/masonry-services/stone-veneer" style={{ color: '#c4a24e', textDecoration: 'none' }} className="hover:underline">Stone Veneer</Link> | Residential &amp; Commercial
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Introduction Section */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto space-y-6 text-lg text-muted-foreground">
                <p>
                  Now it's time for a bit of Passaic County trivia. Passaic sits
                  right in the middle of the County and covers about 3.25 square
                  miles. This makes it one of the most densely populated cities
                  in New Jersey. The area was originally settled by Dutch traders
                  back in{" "}
                  <a
                    href="https://en.wikipedia.org/wiki/Passaic,_New_Jersey"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-secondary hover:underline"
                  >
                    1678 as part of Acquackanonk Township
                  </a>
                  , which also included what is now nearby Clifton and parts of
                  Paterson. The city was officially chartered in 1873, and since
                  then, it has experienced waves of growth, from the textile
                  mills and factories of the late 1800s to the residential
                  neighborhoods you see today along Main Avenue and throughout
                  the Passaic Park section.
                </p>
                <p>
                  Masonry Pro Construction has been working in Passaic and the
                  surrounding area for over 35 years as a licensed masonry
                  contractor. We build and repair masonry steps and stoops,
                  concrete driveways and sidewalks, paver patios, retaining
                  walls, and a full range of masonry and concrete work across the
                  city and areas nearby.
                </p>
                <p>
                  We are located in Totowa, about 10 minutes from most of
                  Passaic, so we are in and out of the city regularly. We have
                  been serving homeowners and property owners throughout{" "}
                  <Link
                    to="/service-areas/passaic-county"
                    className="text-secondary hover:underline"
                  >
                    Passaic County
                  </Link>
                  , for many years. We do a good bit of work in Passaic because
                  of how many older buildings and homes need attention.
                </p>
                <p>
                  What sets Passaic apart from most of the surrounding suburbs is
                  how old a lot of the homes and buildings are, and how tightly
                  everything is built together. Over half of the homes in Passaic
                  were{" "}
                  <a
                    href="https://www.neighborhoodscout.com/nj/passaic/real-estate"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-secondary hover:underline"
                  >
                    built before 1939
                  </a>
                  , and another quarter went up between 1940 and the late 1960s.
                  That means the original masonry on many of these properties
                  (front steps, foundation walls, stoops, sidewalks, driveways)
                  can have 80 to over 100 years of wear. When you combine that
                  age with the freeze-thaw cycling that North Jersey experiences
                  every winter, it explains why so many of the calls we get from
                  Passaic are for step-and-stoop repairs, crumbling concrete, and
                  failing foundation walls.
                </p>
                <p>
                  The soil across most of Passaic is Boonton series{" "}
                  <a
                    href="https://websoilsurvey.nrcs.usda.gov/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-secondary hover:underline"
                  >
                    glacial till
                  </a>
                  , which is a clay-heavy mix that holds water and swells when it
                  freezes. That matters for any kind of masonry or concrete work
                  because the ground movement is what causes settling, shifting,
                  and cracking over time. Of course, we factor all of that into
                  every job we plan in Passaic.
                </p>
                <p>
                  Passaic sits along the Passaic River, and parts of the city
                  closer to the riverbank face additional drainage concerns due
                  to the flat, low-lying terrain. That adds another layer to how
                  we approach base prep, grading, and water management on jobs in
                  those neighborhoods.
                </p>
                <p>
                  If you have a project in mind or something that needs fixing,
                  we can usually come out for a free estimate within a few days.
                  Call or text, and we will set up a time.
                </p>
              </div>
            </div>
          </section>

          {/* Directions Section */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-8">
                  How We Get to Passaic from{" "}
                  <span className="text-gradient-gold">Totowa</span>
                </h2>
                <div className="space-y-6 text-lg text-muted-foreground">
                  <p>
                    We are located at 36 Highview Ave in Totowa, and most parts
                    of Passaic are about a 10-minute drive, depending on which
                    section of the city the job is in.
                  </p>
                  <p>
                    Our most common route is Totowa Road to Route 46 East, then
                    south on Route 21. Route 21 runs right along the western
                    edge of Passaic and has exits for most of the main cross
                    streets, including Brook Avenue, Van Houten Avenue, and Main
                    Avenue. For jobs in the Passaic Park section or near the NJ
                    Transit station, we get off at the Main Avenue exit and head
                    east toward Third Ward Memorial Park. If the job is closer to
                    downtown or the eastern side near Dundee Island Park, we take
                    Route 21 to the Market Street or Monroe Street exits.
                  </p>
                  <p>
                    On the way in, we pass through{" "}
                    <Link
                      to="/service-areas/passaic-county/clifton"
                      className="text-secondary hover:underline"
                    >
                      Clifton
                    </Link>
                    , which is really the only town between Passaic and us on
                    that route. Clifton actually wraps around Passaic on three
                    sides, so a lot of the streets that run into Passaic from the
                    west (like Lexington Avenue, Gregory Avenue, and Paulison
                    Avenue) start in Clifton and cross right into the city. We
                    use those same roads to get to jobs in Garfield, Wallington,
                    and Rutherford, which are all just across the river from
                    Passaic, so we are in this part of the county all the time.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 3: Steps & Stoops — Content LEFT, Image RIGHT */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="max-w-6xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-8">
                  Masonry Steps and Stoops on{" "}
                  <span className="text-gradient-gold">
                    Passaic's Oldest Homes
                  </span>
                </h2>
                <div className="grid lg:grid-cols-5 gap-12 items-center">
                  <div className="lg:col-span-3 space-y-6 text-lg text-muted-foreground">
                    <p>
                      Front steps and stoops are the number-one repair call we
                      get in Passaic, and that makes sense when you think about
                      how old many of these buildings are. The Tudors and Dutch
                      Colonials in the Passaic Park section, the older row-style
                      homes closer to downtown, the two-family and three-family
                      buildings along the side streets off Main Avenue and
                      Broadway, many of them still have the original poured
                      concrete or brick steps that were built when the structure
                      went up. On a home that dates to the 1920s or 1930s, that
                      is close to a century of salt, snow, ice, and freeze-thaw
                      wear.
                    </p>
                    <p>
                      What we typically find when we look at an older stoop in
                      Passaic is that the original footings were poured shallow
                      and without reinforcement, which was common for that era.
                      The concrete was mixed on site, sometimes inconsistently,
                      and little thought was given to drainage around the base.
                      Over time, the clay soil underneath can shift, the stoop
                      can start to settle unevenly, or pull away from the
                      building. Then water can get into the gap between the stoop
                      and the foundation wall. That gap is usually where the real
                      problem lies, because once water gets behind the
                      foundation, it can cause damage that costs much more to
                      address than replacing the steps would have.
                    </p>
                    <p>
                      Because Passaic has so many multi-family properties, we
                      also get a fair number of calls from landlords and property
                      managers who need steps and stoops addressed for safety and
                      liability reasons. A cracked or uneven front stoop on a
                      rental property is a trip hazard, and it is the kind of
                      thing that gets noticed during a code inspection. We work
                      with property owners to get the job done on a timeline that
                      works for their tenants, and we can handle the permit
                      coordination with the city's{" "}
                      <a
                        href="https://www.cityofpassaic.com/247/Code-Enforcement-Zoning"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-secondary hover:underline"
                      >
                        Department of Inspection and Code Enforcement
                      </a>{" "}
                      at 330 Passaic Street.
                    </p>
                  </div>
                  <div className="lg:col-span-2">
                    <div className="aspect-[3/2] rounded-xl overflow-hidden border border-border">
                      <img
                        src={stepsImage}
                        alt="New stone steps and stoop installation leading to a black door in Passaic, Passaic County, NJ. Masonry Pro Construction"
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 4: Concrete Driveways — Image LEFT, Content RIGHT */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-6xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-8">
                  Concrete Driveways and Sidewalks on{" "}
                  <span className="text-gradient-gold">
                    Passaic's Tight Lots
                  </span>
                </h2>
                <div className="grid lg:grid-cols-5 gap-12 items-center">
                  <div className="lg:col-span-2 order-2 lg:order-1">
<div className="aspect-[3/4] rounded-xl overflow-hidden border border-border">
                      <img
                        src={concreteImage}
                        alt="Newly installed concrete sidewalk on a sunny day in Passaic, Passaic County, NJ. Masonry Pro Construction"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                  <div className="lg:col-span-3 order-1 lg:order-2 space-y-6 text-lg text-muted-foreground">
                    <p>
                      Driveway and sidewalk work in Passaic is a little different
                      from what we see in the surrounding suburbs because of how
                      the city is laid out. The lots here are narrow and the
                      homes sit close together, so you are dealing with shared
                      driveways, tight access, and property lines that are
                      sometimes only a few feet from the edge of the concrete. On
                      a lot of the older streets, the original driveway might
                      only be 8 or 9 feet wide, and the sidewalk runs right up
                      to the front of the property with no buffer.
                    </p>
                    <p>
                      A lot of the concrete in Passaic is original to the home,
                      which on the older properties means it has been in place
                      for 70 to 90 years. Surface spalling is usually the first
                      thing a homeowner or property owner notices, where the top
                      layer starts to flake and pit from decades of salt and
                      freeze-thaw exposure. But by the time you can see the
                      spalling, there is usually cracking underneath because
                      water has been working its way into the slab and expanding
                      every winter. The Boonton clay soil makes it worse because
                      the ground itself holds moisture and moves when it freezes,
                      so the slab does not just crack from the top down. It can
                      also heave and settle from underneath.
                    </p>
                    <p>
                      Sidewalk replacement in Passaic can sometimes involve
                      coordination with the city, particularly if the work is on
                      a public sidewalk or near the curb line. The city's code
                      enforcement office handles sidewalk-related violations, and
                      if you have received a notice about a damaged sidewalk, we
                      can come out and give you an estimate for the repair or
                      replacement and walk you through what is required. For
                      driveway work that involves changes to the curb opening or
                      apron, you may need a separate permit from the city
                      engineering department. We sort out which permits apply
                      before we start.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 5: Retaining Walls — Content LEFT, Image RIGHT */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="max-w-6xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-8">
                  Retaining Walls for Grading and Drainage Near the{" "}
                  <span className="text-gradient-gold">Passaic River</span>
                </h2>
                <div className="grid lg:grid-cols-2 gap-12 items-center">
                  <div className="space-y-6 text-lg text-muted-foreground">
                    <p>
                      Passaic does not have the steep hillside lots that you find
                      on the western side of Clifton near Garrett Mountain, but
                      retaining walls are still a common need here for a
                      different reason. A lot of properties in Passaic have grade
                      changes between the yard, the driveway, and the
                      neighboring lot, and on the blocks closer to the Passaic
                      River the terrain is flat and low-lying, which creates
                      drainage problems that a properly built wall can help
                      manage.
                    </p>
                    <p>
                      On the older residential streets, we see a lot of small
                      retaining walls (two to three feet tall) that were built
                      decades ago to hold back a raised yard or separate one
                      property from another. A lot of those walls were
                      dry-stacked or built with minimal reinforcement, and after
                      50 or 60 years of moisture and freeze-thaw the blocks or
                      stones have shifted and the wall is leaning or bulging.
                      When the soil behind the wall is clay, which it is across
                      most of Passaic, the lateral pressure builds every time the
                      ground absorbs water and then freezes. Without proper
                      drainage behind the wall (filter fabric, gravel backfill,
                      weep holes), that pressure has nowhere to go, and the wall
                      eventually gives.
                    </p>
                    <p>
                      For retaining walls over 4 feet tall, the{" "}
                      <a
                        href="https://diyretainingwall.com/nj-requirements/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-secondary hover:underline"
                      >
                        NJ Uniform Construction Code
                      </a>{" "}
                      requires engineering calculations from a licensed
                      professional engineer. Walls under that height still need
                      to meet the city's zoning and construction permit
                      requirements. We handle the permit coordination with
                      Passaic's building department so you do not have to figure
                      that process out on your own.
                    </p>
                  </div>
                  <div>
                    <img
                      src={retainingWallImage}
                      alt="Tiered retaining wall installation with landscaping in Passaic, Passaic County, NJ by Masonry Pro Construction"
                      width={880}
                      height={1173}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-auto rounded-xl border border-border object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 6: Paver Patios — Image LEFT, Content RIGHT */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-6xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-8">
                  Paver Patios for Smaller Yards in{" "}
                  <span className="text-gradient-gold">
                    Passaic and Passaic Park
                  </span>
                </h2>
                <div className="grid lg:grid-cols-5 gap-12 items-center">
<div className="lg:col-span-2 order-2 lg:order-1">
<div className="aspect-[3/4] rounded-xl overflow-hidden border border-border">
                      <img
                        src={patioImage}
                        alt="Newly paved patio and walkway between two brick buildings in Passaic, Passaic County, NJ. Masonry Pro Construction"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                  <div className="lg:col-span-3 order-1 lg:order-2 space-y-6 text-lg text-muted-foreground">
                    <p>
                      Passaic does not have the deep suburban backyards that you
                      find in Wayne or Montclair, but that does not mean patio
                      projects are not happening here. We get calls from
                      homeowners in the Passaic Park section and throughout the
                      residential streets who want to make better use of whatever
                      outdoor space they do have. Even a 200 or 300 square foot
                      paver patio can change how a family uses their backyard,
                      and on a smaller lot the design and layout matter even more
                      because you are working with less room.
                    </p>
                    <p>
                      The same soil issues that affect driveways and steps in
                      Passaic apply to patios. Boonton clay holds moisture and
                      moves when it freezes, so a patio that was laid on a thin
                      base or without proper drainage will start to shift and
                      settle within a few years because the ground underneath is
                      doing the moving for it. We dig deeper than a lot of
                      contractors and compact the aggregate base in lifts,
                      because on clay soil you do not get a second chance to get
                      the foundation right. On the smaller Passaic lots, managing
                      where the water goes after it runs off the patio is just as
                      important as the base itself, since there is less yard to
                      absorb it and neighboring properties are often right there.
                    </p>
                    <p>
                      We also hear from homeowners in Passaic who searched for
                      stamped concrete and ended up finding us. We will be
                      upfront about it: stamped concrete does not hold up well in
                      North Jersey because the freeze-thaw cycling cracks the
                      surface, and once the seal breaks the deterioration picks
                      up fast. Pavers handle the same conditions better because
                      each unit can flex independently, and if one does crack you
                      can pull it and replace just that piece without redoing the
                      whole patio.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 7: Services List */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Services We Provide in{" "}
                  <span className="text-gradient-gold">Passaic</span>
                </h2>
                <p className="text-lg text-muted-foreground mb-8">
                  Here is a quick look at the work we do across Passaic's
                  neighborhoods. Each service links to a page with more detail on
                  materials, process, and what to expect.
                </p>
                <ul className="space-y-4 text-lg text-muted-foreground">
                  <li>
                    <Link
                      to="/masonry-services/steps-stoops"
                      className="text-secondary hover:underline font-bold"
                    >
                      Masonry Steps &amp; Stoops:
                    </Link>{" "}
                    New construction and replacement steps for older homes and
                    multi-family buildings, built on proper footings.
                  </li>
                  <li>
                    <Link
                      to="/concrete-services/driveways"
                      className="text-secondary hover:underline font-bold"
                    >
                      Concrete Driveway Installation:
                    </Link>{" "}
                    Full driveway replacement with reinforcement and drainage,
                    designed for tight urban lots.
                  </li>
                  <li>
                    <Link
                      to="/concrete-services/sidewalks"
                      className="text-secondary hover:underline font-bold"
                    >
                      Concrete Sidewalk Installation:
                    </Link>{" "}
                    Sidewalk repair and replacement, including coordination with
                    city code enforcement notices.
                  </li>
                  <li>
                    <Link
                      to="/paving-services/paver-patios"
                      className="text-secondary hover:underline font-bold"
                    >
                      Paver Patio Installation:
                    </Link>{" "}
                    Custom patios with proper base prep for clay soil, sized for
                    Passaic's smaller residential lots.
                  </li>
                  <li>
                    <Link
                      to="/masonry-services/retaining-walls"
                      className="text-secondary hover:underline font-bold"
                    >
                      Retaining Wall Installation:
                    </Link>{" "}
                    Structural and decorative walls with drainage and engineering
                    coordination where required.
                  </li>
                  <li>
                    <Link
                      to="/masonry-services/retaining-wall-repair"
                      className="text-secondary hover:underline font-bold"
                    >
                      Retaining Wall Repair:
                    </Link>{" "}
                    Fixing leaning, cracked, or bulging walls and addressing the
                    drainage problems behind them.
                  </li>
                  <li>
                    <Link
                      to="/masonry-services/stone-veneer"
                      className="text-secondary hover:underline font-bold"
                    >
                      Stone Veneer Installation:
                    </Link>{" "}
                    Facade upgrades and foundation wraps for older Passaic homes.
                  </li>
                  <li>
                    <Link
                      to="/waterproofing-services/basement-waterproofing"
                      className="text-secondary hover:underline font-bold"
                    >
                      Basement Waterproofing:
                    </Link>{" "}
                    Interior and exterior waterproofing for Passaic basements
                    affected by clay soil and river-area drainage.
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Section 8: FAQ Cards */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
                Frequently Asked Questions About Masonry Work in{" "}
                <span className="text-gradient-gold">Passaic</span>
              </h2>
              <div className="grid md:grid-cols-2 gap-6 max-w-6xl mx-auto">
                {faqs.map((faq, index) => (
                  <div
                    key={index}
                    className="bg-background rounded-xl p-6 border border-border"
                  >
                    <h3 className="text-lg font-bold mb-3">{faq.question}</h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Citations Accordion */}
          <section className="py-12">
            <div className="container mx-auto px-4 max-w-4xl">
              <Accordion type="single" collapsible>
                <AccordionItem value="citations" className="border-border">
                  <AccordionTrigger className="text-sm text-muted-foreground hover:no-underline">
                    Sources &amp; Citations
                  </AccordionTrigger>
                  <AccordionContent>
                    <ol className="list-decimal list-inside space-y-3 text-sm text-muted-foreground">
                      <li>
                        Passaic, New Jersey – History and founding (1678,
                        Acquackanonk Township). Wikipedia:
                        en.wikipedia.org/wiki/Passaic,_New_Jersey
                      </li>
                      <li>
                        Housing age data for Passaic, NJ.
                        NeighborhoodScout:
                        neighborhoodscout.com/nj/passaic/real-estate
                      </li>
                      <li>
                        Boonton series glacial till (clay-heavy soil in Passaic
                        County). USDA NRCS Web Soil Survey:
                        websoilsurvey.nrcs.usda.gov
                      </li>
                      <li>
                        Retaining wall permit threshold (4 ft engineering
                        requirement). NJ Uniform Construction Code:
                        diyretainingwall.com/nj-requirements
                      </li>
                      <li>
                        City of Passaic Department of Inspection and Code
                        Enforcement:
                        cityofpassaic.com/247/Code-Enforcement-Zoning
                      </li>
                    </ol>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </section>

          {/* Section 9: CTA with Contact Form */}
          <CTASection
            serviceName="Passaic Masonry Project"
            heading="Get a Free Estimate on Your"
            highlightedHeading="Passaic Masonry Project"
          />
          <section className="py-12 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto text-center space-y-2">
                <p className="text-xl font-bold">Masonry Pro Construction</p>
                <p className="text-muted-foreground">36 Highview Ave, Totowa, NJ 07512</p>
                <a
                  href="tel:9737252395"
                  className="text-secondary hover:underline text-lg font-semibold block"
                >
                  (973) 725-2395
                </a>
                <p className="text-sm text-muted-foreground italic mt-4">
                  NJ HIC License{" "}
                  <a href="https://newjersey.mylicense.com/verification/Search.aspx?facility=Y" target="_blank" rel="noopener noreferrer" className="hover:text-secondary transition-colors underline">#13VH06777200</a>
                </p>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}
